import React, {
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AccountContext, SendMethod } from "../../store/AccountContext";
import { Navigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { Alert, AlertTitle } from "@mui/material";
import PhoneInput, {
  TwoStepPhoneInputHandle,
} from "../common/TwoStepPhoneInput";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import ReactCodeInput from "react-verification-code-input";
import { Box, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  // WhatsappButton,
  SmsButton,
  PrimaryButton,
  SmsButtonSecondary,
} from "../common/buttons";
import { consumeReturnPath } from "../../utils/returnPath";
import { validatePhoneClientSide } from "../../utils/PhoneValidator";
import { PhoneValidationSuggestion, maskE164 } from "../../utils/errorMessages";
import useLogger from "../../hooks/useLogger";
import { LogEventCategory, AccountActionEvents } from "../../api/entities/Log";

/**
 * PM5 T2 (money path, batch "signin") — converted from MUI v4 + `makeStyles`
 * to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** This is the mobile-number / one-time-passcode
 * login form. The conversion is styling-only: no handler, validator, log
 * event, state value, prop, condition or DOM node moved. Acceptance bar is
 * visual parity — every rule below is the v4 rule it replaces, and each place
 * a v5+ library default would have changed the rendering is compensated for
 * with a comment saying why.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `submit`, `center`, `forgotPassword`,
 * `button` and `secSubmit` were declared in the v4 `useStyles` object but never
 * referenced from the JSX, so they rendered nothing. Carrying them forward
 * would be inventing styles, not porting them.
 */

/* ---------------------------------------------------------------------- *
 * styled() — host / third-party elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().form — applied to a bare <div> and to a bare <form>, neither
// of which has an `sx` prop. One style object, two host tags.
const formStyles = ({ theme }: { theme: Theme }) => ({
  width: "100%", // Fix IE 11 issue.
  marginTop: theme.spacing(1),
});
const FormBody = styled("div")(formStyles);
const FormElement = styled("form")(formStyles);

// was: useStyles().verificationCodeContainer — `ReactCodeInput` is a
// third-party class component with no `sx` prop; it does apply an incoming
// `className` to its root div, so `styled()` is the port (guide §1.2 row 2).
// The nested rules target the bare `input` elements the library renders, so
// they are element selectors, not `.Mui*` ones — the §1.2b class-prefix trap
// does not apply.
//
// The cast stands in for the v4 call site's `{/* @ts-ignore */}`: the library
// ships no typings, so TS infers them from its JS bundle and gets the default
// export's shape wrong. Same `any`-shaped props as before, expressed once at
// the wrapper instead of suppressed at the element.
const VerificationCodeInput = styled(
  ReactCodeInput as unknown as React.ComponentType<any>,
)({
  width: "100% !important",
  fontWeight: 600,
  "& input": {
    height: "56px !important",
    width: "15.6% !important",
    borderTop: "none",
    borderRight: "none",
    borderLeft: "none",
    borderTopLeftRadius: "0px !important",
    borderBottomLeftRadius: "0px !important",
    borderTopRightRadius: "0px !important",
    borderBottomRightRadius: "0px !important",
    borderBottom: 0,
    backgroundColor: "#444",
    margin: "0 0.5%",
    fontSize: "2rem",
    fontWeight: 500,
    color: "#fff",
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    "&:focus": {
      border: "1px solid #99ca3c",
      caretColor: "#fff",
      "&:last-child": {
        borderRight: "1px solid #99ca3c",
      },
    },
    "&:last-child": {
      borderRight: "none",
    },
  },
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was implemented as
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaced that with `gap` on the container and **no** padding on the items
 * (guide §2, "Grid `spacing` implementation"). The two are only geometrically
 * identical when nothing else touches the container's box, and both containers
 * in this file break that: the first carries an inline `marginTop` that
 * overrides the top half of the negative margin, and the second has a
 * full-width `<Alert>` as a direct child that is NOT a Grid item (so it never
 * got the item padding, but would now pick up the container's `gap`).
 *
 * So the port carries v4's own declarations verbatim rather than re-deriving
 * the geometry: `spacing` is omitted (v9 default 0 ⇒ `gap: 0px`) and each item
 * carries the 8px padding itself. Identical by construction, not by arithmetic
 * — and an inline `style` still overrides one half of the shorthand exactly as
 * it did in v4.
 */
const v4GridContainerSx: SxProps<Theme> = {
  width: "calc(100% + 16px)",
  margin: "-8px",
};
const v4GridItemSx: SxProps<Theme> = { padding: "8px" };

// The three `<Grid item>`s that sit OUTSIDE any `<Grid container>` (siblings of
// the container, inside `FormBody`). In v4 that still emitted the item's
// `flex-basis`/`max-width` percentages; in v9 a Grid's width comes from CSS
// custom properties published by a container, so with no container the calc()
// is invalid and the element falls back to full width. Guide §2 row 3: port an
// orphan item as a Box carrying the literal v4 declarations, never as a Grid.
const orphanGridItemSx: SxProps<Theme> = {
  boxSizing: "border-box",
  margin: 0,
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

// was: useStyles().bodyText
const bodyTextSx: SxProps<Theme> = {
  color: "common.white",
  marginBottom: "10px",
  textAlign: "center",
};

// was: useStyles().divider
//
// ♦ `borderRadius` is NOT a plain length in `sx`: a bare number is multiplied
// by `theme.shape.borderRadius` (4 here), so `borderRadius: 5` would render
// **20px**, not the 5px the v4 `makeStyles` rule emitted. Ported as the literal
// string, which `sx` passes straight through. (New guide §2 row, batch signin.)
const dividerSx: SxProps<Theme> = {
  backgroundColor: "white",
  borderRadius: "5px",
  height: "2px",
  marginTop: 0,
  marginBottom: 1,
};

// was: useStyles().validationError
const validationErrorSx: SxProps<Theme> = { marginBottom: 2 };

// was: useStyles().backdrop
const backdropSx: SxProps<Theme> = { zIndex: 100000000, color: "#fff" };

// was: useStyles().secButton. This one pins its own `color`, so v5+'s change of
// the Button default colour from `default` to `primary` (guide §2) cannot reach
// its label.
const secButtonSx: SxProps<Theme> = (theme) => ({
  margin: theme.spacing(1.5, 0, 2),
  borderColor: "#e9e9e9",
  color: "#e9e9e9",
  [theme.breakpoints.up("sm")]: {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
  },
  "&:hover": {
    borderColor: "#fff",
    color: "#fff",
  },
});

// was: useStyles().formFieldSetLarge, merged with the Grid item padding above.
const formFieldSetLargeItemSx: SxProps<Theme> = (theme) => ({
  padding: "8px",
  margin: theme.spacing(2, 0, 0.5, 0),
});

// was: useStyles().thirdHeader
const thirdHeaderSx: SxProps<Theme> = (theme) => ({
  color: "background.ffGreen",
  fontSize: "1.6rem",
  textAlign: "center",
  fontWeight: 500,
  marginBottom: 2,
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
});

// was: useStyles().submittedMobileNumber
const submittedMobileNumberSx: SxProps<Theme> = {
  marginTop: 3,
  marginBottom: 3,
  fontWeight: 600,
  textAlign: "center",
  fontSize: "1.4rem",
  color: "common.white",
};

const MobileSignIn = ({
  toggleLoginMethod,
}: {
  toggleLoginMethod: () => void;
}) => {
  const {
    loginWithPhone,
    loginWithPhoneVerificationToken,
    loading,
    resetErrors,
    loginError,
    phoneSuggestion: serverSuggestion,
    resetPhoneSuggestion,
    loggedIn,
    waitingOnVerificationCode,
  } = useContext(AccountContext);
  const { logEvent } = useLogger();
  const [sendMethod, setSendMethod] = useState<"sms" | "whatsapp">("sms");
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [countryCode, setCountryCode] = useState<string>();
  const [disabledLogin, setDisabledLogin] = useState(true);
  const [verificationCode, setVerificationCode] = useState<string>();
  // Client-side phone validation state (#284). `clientError` is set when
  // we reject the input before the API call; `clientSuggestion` carries
  // the optional "Use this number" correction. Server-side rejections
  // come through `serverSuggestion` from AccountContext and merge into
  // the same UI.
  const [clientError, setClientError] = useState<string | undefined>();
  const [clientSuggestion, setClientSuggestion] = useState<
    PhoneValidationSuggestion | undefined
  >();
  // Imperative ref to TwoStepPhoneInput. Used to seed the field when a
  // #284 phone-validation suggestion is applied. Goes via the component's
  // `seedValue` API which queryselects the input — passing a ref directly
  // to react-phone-input-2 collapses the library's own ref and crashes it.
  const phoneInputRef = useRef<TwoStepPhoneInputHandle | null>(null);
  // Captured once, the first render after the phone → code flow succeeds:
  // the deep link the member was heading for before the auth gate bounced
  // them (#571). Reading it consumes (clears) it, so the ref guard makes
  // sure that happens exactly once and stays stable across re-renders.
  const redirectTarget = useRef<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => resetErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitSms = useCallback(async () => {
    if (!countryCode || !phoneNumber) return;

    // Client-side validation (#284) runs before the API call to catch
    // obvious typos (double country code, length issues) without paying
    // for a network round-trip. Server is the authoritative backstop.
    const clientCheck = validatePhoneClientSide(countryCode, phoneNumber);
    if (!clientCheck.valid) {
      setClientError(clientCheck.userMessage);
      setClientSuggestion(clientCheck.suggestion);
      logEvent({
        category: LogEventCategory.ACCOUNT_ACTION,
        event: AccountActionEvents.PHONE_VALIDATION_REJECTED,
        data: {
          surface: "login",
          source: "client",
          rejectionReason: clientCheck.suggestion?.rejectionReason,
          suggestedE164: clientCheck.suggestion?.suggestedE164
            ? maskE164(clientCheck.suggestion.suggestedE164)
            : undefined,
        },
      });
      return;
    }

    setClientError(undefined);
    setClientSuggestion(undefined);
    setSendMethod("sms");
    await loginWithPhone(countryCode, phoneNumber, "sms");
  }, [loginWithPhone, phoneNumber, countryCode, logEvent]);

  // const onSubmitWhatsapp = useCallback(async () => {
  //   if (!countryCode || !phoneNumber) return;

  //   setSendMethod("whatsapp");
  //   await loginWithPhone(countryCode, phoneNumber, "whatsapp");
  // }, [loginWithPhone, phoneNumber, countryCode]);

  const onChange = (nextCountryCode: string, nextPhoneNumber: string) => {
    setDisabledLogin(false);
    setPhoneNumber(nextPhoneNumber);
    setCountryCode(nextCountryCode);
    if (clientError) setClientError(undefined);
    if (clientSuggestion) setClientSuggestion(undefined);
    if (serverSuggestion) resetPhoneSuggestion();
  };

  const activeSuggestion = clientSuggestion || serverSuggestion;

  const applySuggestion = () => {
    if (!activeSuggestion) return;
    const {
      suggestedPhoneNumber,
      suggestedCallingCode,
      suggestedE164,
      rejectionReason,
    } = activeSuggestion;
    if (suggestedPhoneNumber) setPhoneNumber(suggestedPhoneNumber);
    if (suggestedCallingCode) setCountryCode(suggestedCallingCode);

    if (suggestedE164) phoneInputRef.current?.seedValue(suggestedE164);

    logEvent({
      category: LogEventCategory.ACCOUNT_ACTION,
      event: AccountActionEvents.PHONE_VALIDATION_SUGGESTION_APPLIED,
      data: {
        surface: "login",
        source: clientSuggestion ? "client" : "server",
        rejectionReason,
        suggestedE164: suggestedE164 ? maskE164(suggestedE164) : undefined,
      },
    });
    setClientError(undefined);
    setClientSuggestion(undefined);
    if (serverSuggestion) resetPhoneSuggestion();
  };

  // Log server-side rejections that come back via AccountContext. These are
  // typos that slipped past the client validator (e.g. landline numbers,
  // since the `min` libphonenumber metadata can't tell mobile from landline).
  useEffect(() => {
    if (!serverSuggestion) return;
    logEvent({
      category: LogEventCategory.ACCOUNT_ACTION,
      event: AccountActionEvents.PHONE_VALIDATION_REJECTED,
      data: {
        surface: "login",
        source: "server",
        rejectionReason: serverSuggestion.rejectionReason,
        code: serverSuggestion.code,
        suggestedE164: serverSuggestion.suggestedE164
          ? maskE164(serverSuggestion.suggestedE164)
          : undefined,
      },
    });
  }, [serverSuggestion, logEvent]);

  const handleVerficationCodeSubmit = async () => {
    if (!verificationCode) return;

    await loginWithPhoneVerificationToken(verificationCode);
  };

  const resendVerificationCode =
    (method: SendMethod): MouseEventHandler =>
    (evt) => {
      if (!countryCode || !phoneNumber) return;
      evt.preventDefault();
      loginWithPhone(countryCode, phoneNumber, sendMethod);
    };

  if (loggedIn) {
    if (redirectTarget.current === null) {
      redirectTarget.current = consumeReturnPath() || "/videos";
    }
    return <Navigate to={redirectTarget.current} replace />;
  }

  return (
    <>
      {!waitingOnVerificationCode && (
        <>
          <Typography
            component="p"
            variant="body2"
            sx={bodyTextSx}
            style={{ marginBottom: "20px" }}
          >
            Log In with your Mobile Number.
            <br />
            <br />
            We&apos;ll send a <b>One Time Passcode</b> via SMS which you can use
            to log into your account.
          </Typography>
          <FormBody>
            <Grid
              container
              sx={v4GridContainerSx}
              style={{ marginTop: "20px" }}
            >
              <Grid size={{ xs: 12, sm: 12 }} sx={formFieldSetLargeItemSx}>
                {(loginError || clientError) && (
                  <Alert
                    sx={validationErrorSx}
                    severity="error"
                    variant="filled"
                    style={{ marginBottom: "16px" }}
                  >
                    {clientError || loginError}
                  </Alert>
                )}
                <PhoneInput
                  ref={phoneInputRef}
                  onChange={onChange}
                  onFocus={resetErrors}
                  disabled={loading}
                />
                {activeSuggestion && activeSuggestion.suggestedE164 && (
                  <Box sx={{ mt: 2 }}>
                    <PrimaryButton
                      title={`Use ${activeSuggestion.suggestedE164}`}
                      onClick={applySuggestion}
                    />
                  </Box>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                {loading && (
                  <Backdrop sx={backdropSx} open={true}>
                    <CircularProgress color="primary" />
                  </Backdrop>
                )}

                <Box sx={{ flex: 1, flexDirection: "row" }}>
                  <SmsButton
                    title="Log In with SMS"
                    onClick={onSubmitSms}
                    disabled={disabledLogin || loading}
                  />
                  {/* <WhatsappButton
                    title={"Log In with WhatsApp"}
                    onClick={onSubmitWhatsapp}
                    disabled={disabledLogin || loading}
                  /> */}
                </Box>
              </Grid>
            </Grid>
            <Box sx={orphanGridItemSx}>
              <Divider sx={dividerSx} title="or" />
            </Box>
            <Box sx={orphanGridItemSx}>
              <Button
                fullWidth
                onClick={toggleLoginMethod}
                size="medium"
                variant="outlined"
                sx={secButtonSx}
              >
                Switch to Email
              </Button>
            </Box>
          </FormBody>
        </>
      )}
      {waitingOnVerificationCode && (
        <>
          <Typography component="h3" variant="h3" sx={thirdHeaderSx}>
            Verify Mobile
          </Typography>
          <Typography
            variant="body2"
            sx={bodyTextSx}
            align="center"
            style={{ textAlign: "center" }}
          >
            We need to make sure it&apos;s really you, so we&apos;ve sent a 6
            digit code to the following mobile number. Please enter it below.
          </Typography>
          <Typography variant="body1" sx={submittedMobileNumberSx}>
            +{countryCode}
            {phoneNumber}
          </Typography>
          <FormElement>
            <Grid container sx={v4GridContainerSx}>
              {loginError && (
                <Alert
                  severity="error"
                  variant="filled"
                  style={{ marginBottom: "12px", width: "100%" }}
                >
                  <AlertTitle style={{ color: "#fff", marginTop: "-0" }}>
                    Invalid Verification Code
                  </AlertTitle>
                  Please re-enter code of request another with the &apos;Resend
                  Code&apos; button.
                </Alert>
              )}
              <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                <VerificationCodeInput
                  type="number"
                  fields={6}
                  value={verificationCode}
                  onComplete={(code: string) => setVerificationCode(code)}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
                {loading && (
                  <Backdrop sx={backdropSx} open={true}>
                    <CircularProgress color="primary" />
                  </Backdrop>
                )}
                <PrimaryButton
                  title="Verify"
                  onClick={handleVerficationCodeSubmit}
                  disabled={loading || !verificationCode}
                />
              </Grid>
              <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
                <Divider sx={dividerSx} />
              </Grid>
              <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
                {/* <WhatsappButton
                  title="Resend code with WhatsApp"
                  onClick={resendVerificationCode(
                    sendMethod === "sms" ? "whatsapp" : "sms"
                  )}
                /> */}
                <SmsButtonSecondary
                  title="Resend code with SMS"
                  onClick={resendVerificationCode("sms")}
                />
              </Grid>
            </Grid>
          </FormElement>
        </>
      )}
    </>
  );
};

export default MobileSignIn;
